<template>
  <div
    data-component="layouts/ShortOverlay"
    class="short-overlay"
    :class="{
      'short-overlay--current': state.current,
    }"
  >
    <div v-if="state.current" class="short-overlay__inner">
      <div
        class="short-overlay__content"
      >
        <button
          class="short-overlay__button"
          :class="{ 'short-overlay__button--no-show-left': !state.showLeft }"
          @click="onPrev"
        >
          <AppIconLoader
            name="arrowLongLeft"
            class="short-overlay__icon"
            variant="extra-large"
            aria-hidden="true"
          />
        </button>
        <button
          class="short-overlay__content-button"
          :class="{ 'short-overlay__content-button--no-show-right': !state.showRight }"
          @click="onNext"
        >
          <AppIconLoader
            name="arrowLong"
            class="short-overlay__content-button-icon"
            variant="extra-large"
            aria-hidden="true"
          />
        </button>
      </div>
      <button class="short-overlay__inner-button" @click="onClose">
        <AppIconLoader name="close" aria-hidden="true" variant="large" class="short-overlay__inner-button-icon" />
      </button>
    </div>
    <div
      v-if="state.current"
      class="short-overlay__short-card"
      :data-domain="state.current.domain?.color?.name"
      :style="dynStyle"
    >
      <div class="short-overlay__short-card-content">
        <AppYoutubeThumbnail
          v-if="state.current.youtube_short_id"
          :source-id="state.current.youtube_short_id as string"
          size="large"
          class="short-overlay__youtube-thumbnail"
          :cover="true"
        />
        <iframe
          v-if="state.current.youtube_short_id"
          loading="eager"
          :src="`https://youtube.com/embed/${state.current.youtube_short_id}?rel=0&controls=0&showinfo=0&modestbranding=1&autoplay=1`"
          class="short-overlay__iframe"
          :class="{ 'short-overlay__iframe--toggle': state.toggle }"
        />
        <LazyAppStreamingVideo
          v-if="!state.current.youtube_short_id && state.current.hls && state.current.mp4"
          :key="`streaming-video-${state.idx}`"
          :hls="state.current.hls"
          :mp4="state.current.mp4"
          :loop="true"
          :interactive="true"
          :auto-play="true"
          :muted="false"
          :content-fit="'cover'"
          class="short-overlay__streaming-video"
        />
      </div>

      <div
        class="short-overlay__wrapper"
      >
        <button class="short-overlay__wrapper-button" @click="onClose">
          <AppIconLoader name="close" aria-hidden="true" variant="large" class="short-overlay__inner-button-icon" />
        </button>
        <!--        <ul v-if="state.current.belongs_to[0]" class="flex flex-wrap justify-center gap-1 lg:gap-2">-->
        <!--          <li>-->
        <!--            <AppTagButton v-if="state.current.belongs_to[0]" :data="state.current.belongs_to[0]"-->
        <!--                          class="!pointer-events-auto tag-button-outline" @click="onClose"/>-->
        <!--          </li>-->
        <!--        </ul>-->
        <ul>
          <li v-if="state.current?.external_url && state.current?.external_title">
            <AppButton
              :title="state.current.external_title"
              :url="state.current?.external_url"
              size="small"
              target="_blank"
              class="short-overlay__button-2"
              @click="onClose"
            />
          </li>
          <li />
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUpdated, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useShortsStore } from '~/store/shorts'
import { isMobile, joinSlugId, useHeadData, usePopState, useTracking } from '#imports'
import { getCollection } from '~/services/custom-endpoints'
import { TPage, TShortPage } from '~/types/page-types'
import { usePageStore } from '~/store/page'

type TState = {
  idx: number
  current?: TShortPage
  showLeft: boolean
  showRight: boolean
  muted: boolean
  toggle: boolean
}

const state = reactive<TState>({
  idx: 0,
  current: undefined,
  showLeft: false,
  showRight: false,
  muted: true,
  toggle: false
})

const shortsStore = useShortsStore()
const pageStore = usePageStore()

const router = useRouter()

// update the head data & analytics
watch(
  () => state.current,
  (newValue) => {
    if (newValue) {
      state.toggle = false
      window.setTimeout(() => {
        state.toggle = true
      }, 300)
    }

    const curPage = newValue || pageStore.getCurrentPage
    curPage.collection = 'short_page'
    useHeadData(curPage as TPage)
    useTracking(curPage as TPage)
  }
)

onMounted(() => {
  const short = router.currentRoute.value.query?.short
  if (short) {
    onShowShort(short.toString())
  }
})

onUpdated(() => {
  setTimeout(() => {
    shortsStore.dimensions.translateX = '-50%'
    shortsStore.dimensions.translateY = '-50%'
    shortsStore.dimensions.height = isMobile.any() ? '100%' : '648px'
    shortsStore.dimensions.width = isMobile.any() ? '100%' : '328px'
  }, 200)
})

const onClose = function () {
  shortsStore.dimensions.width = '202px'
  shortsStore.dimensions.height = '360px'

  setTimeout(() => {
    shortsStore.slugId = undefined
    document.body.style.overflow = 'auto'
    history.pushState({ short: shortsStore.slugId }, state.current?.title, location.pathname)
  }, 200)
}

const onPrev = function () {
  shortsStore.slugId = joinSlugId(shortsStore.shorts[state.idx - 1])
}
const onNext = function () {
  shortsStore.slugId = joinSlugId(shortsStore.shorts[state.idx + 1])
}

function onUpdateState () {
  state.idx = shortsStore.shorts.findIndex((short) => {
    return joinSlugId(short) === shortsStore.slugId
  })
  state.current = shortsStore.shorts[state.idx]
  state.showLeft = state.idx > 0
  state.showRight = state.idx < shortsStore.shorts.length - 1
  if (shortsStore.slugId) {
    document.body.style.overflow = 'hidden'
    history.pushState({ short: shortsStore.slugId }, state.current?.title, `${location.pathname}?short=${shortsStore.slugId}`)
  }
}

function onShowShort (short: string) {
  shortsStore.slugId = short
}

watch(
  () => shortsStore.shorts,
  () => {
    onUpdateState()
  }
)

const dynStyle = computed(() => {
  const style = []
  style.push(`width: ${shortsStore.dimensions.width};`)
  style.push(`height: ${shortsStore.dimensions.height};`)
  style.push(`transform: translate(${shortsStore.dimensions.translateX}, ${shortsStore.dimensions.translateY});`)
  return style.join(' ')
})

watch(
  () => shortsStore.slugId,
  async () => {
    if (shortsStore.shorts.length === 0) {
      const {
        data,
        error
      } = await getCollection('short_page', { status: 'published' }, ['*', 'domain.*.*', 'belongs_to.*.*'], ['-date_created'])
      if (error.value) {
        throw new Error(error.value.message)
      }
      if (data.value?.data) {
        shortsStore.setShorts(data.value.data as TShortPage[])
      }
    } else {
      onUpdateState()
    }
  }
)

usePopState(() => {
  if (state.current) {
    onClose()
  }
})
</script>

<style scoped lang="postcss">
.short-overlay {
  pointer-events: none;
  position: fixed;
  left: 0px;
  top: 0px;
  z-index: 40;
  display: block;
  height: 100%;
  width: 100%;
  background-color: #000;
  opacity: 0;
  transition-property: all;
  transition-duration: 300ms;
  transition-timing-function: cubic-bezier(0.4, 0, 1, 1);

  &--current {
    pointer-events: auto !important;
    opacity: 1;
  }

  &__inner {
    position: relative;
    height: 100%;
    width: 100%;
  }

  &__content {
    position: absolute;
    top: 50%;
    grid-column: 1 / -1;
    display: flex;
    width: 100%;
    transform: translateY(-50%);
    align-items: center;
    justify-content: center;

    @media (--lg) {
      padding-left: 2.5rem;
      padding-right: 2.5rem;
    }

    @media (--xl) {
      justify-content: space-between;
    }
  }

  &__button {
    display: none;
    cursor: pointer;
    transition-duration: 100ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

    &--no-show-left {
      pointer-events: none;
      opacity: 0;
    }

    @media (--xl) {
      display: block;
    }
  }

  &__icon {
    color: var(--color-blue-aqua);

    &:hover {
      transform: translateX(-1rem);
    }
  }

  &__content-button {
    display: none;
    cursor: pointer;
    transition-duration: 100ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

    &--no-show-right {
      pointer-events: none;
      opacity: 0;
    }

    @media (--xl) {
      display: block;
    }
  }

  &__content-button-icon {
    cursor: pointer;
    color: var(--color-blue-aqua);
    transition-duration: 100ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: translateX(1rem);
    }
  }

  &__inner-button {
    position: absolute;
    right: 40px;
    top: 1.25rem;
    z-index: 50;
    display: none;

    @media (--md) {
      display: block;
    }
  }

  &__inner-button-icon {
    color: var(--color-blue-aqua);
  }

  &__short-card {
    position: fixed;
    left: 50%;
    top: 50%;
    z-index: 10;
    display: grid;
    aspect-ratio: 9/16;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    overflow: clip;
    border-radius: 0.375rem;
    transition-property: all;
    transition-duration: 300ms;
    transition-timing-function: cubic-bezier(0.4, 0, 1, 1);

    @media (--md) {
      border-width: 3px;
    }

    @media (--lg) {
      border-radius: 1.5rem;
    }

    &__content {
      &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 20;
        pointer-events: none;
        /* background: linear-gradient(180deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.00) 100%), linear-gradient(16deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.00) 55.96%); */
      }
    }

    &[data-domain="violet"] {
      border-color: var(--color-violet);
      color: var(--color-violet);
    }

    &[data-domain="yellow"] {
      border-color: var(--color-yellow);
      color: var(--color-yellow);
    }

    &[data-domain="red"] {
      border-color: var(--color-red);
      color: var(--color-red);
    }
  }

  &__short-card-content {
    position: relative;
    grid-column: 1 / -1;
    grid-row-start: 1;
    height: 100%;
  }

  &__youtube-thumbnail {
    position: absolute;
    top: 0px;
    aspect-ratio: 9/16;
    height: 100%;
    overflow: clip;
  }

  &__iframe {
    position: relative;
    z-index: 20;
    aspect-ratio: 9/16;
    height: 100%;
    width: 100%;
    overflow: clip;
    border-radius: 0.375rem;
    opacity: 0;
    transition-property: all;
    transition-delay: 300ms;
    transition-duration: 300ms;
    transition-timing-function: cubic-bezier(0.4, 0, 1, 1);

    &--toggle {
      opacity: 1;
    }
  }

  &__streaming-video {
    display: flex;
    width: 100%;
    flex: 1 1 auto;
    justify-content: stretch;
    overflow: clip;
  }

  &__wrapper {
    pointer-events: none;
    position: absolute;
    top: 0px;
    z-index: 20;
    display: flex;
    height: 100%;
    width: 100%;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-end;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-bottom: 2rem;
    padding-top: 1.5rem;
  }

  &__wrapper-button {
    pointer-events: auto !important;
    position: absolute;
    right: 16px;
    top: 1rem;
    z-index: 50;
    display: block;

    @media (--md) {
      display: none;
    }
  }

  &__button-2 {
    pointer-events: auto !important;
  }
}
</style>
